/**
 * Servicio para gestión del módulo de Ventas
 * Incluye: Entregas, Checklist Documentos, Encuestas y Certificados
 * Formatos: AMX-VTA-FR-07, AMX-VTA-FR-08, AMX-VTA-FR-09, AMX-VTA-FR-11
 */

import api from './api.js';

class VentasService {
  // =====================================================
  // ENTREGAS DE TRACTOR (AMX-VTA-FR-07)
  // =====================================================

  /**
   * Listar entregas con filtros
   */
  async listarEntregas(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/ventas/entregas', cleanParams);
  }

  /**
   * Obtener entrega por ID
   */
  async obtenerEntrega(id) {
    return await api.get(`/ventas/entregas/${id}`);
  }

  /**
   * Crear nueva entrega
   */
  async crearEntrega(data) {
    return await api.post('/ventas/entregas', data);
  }

  /**
   * Actualizar item de inspección física
   */
  async actualizarInspeccion(itemId, data) {
    const response = await fetch(`${api.baseUrl}/ventas/entregas/inspeccion/${itemId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Actualizar sistema de funcionamiento
   */
  async actualizarSistema(sistemaId, data) {
    const response = await fetch(`${api.baseUrl}/ventas/entregas/sistema/${sistemaId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Actualizar nivel de fluido
   */
  async actualizarFluido(fluidoId, data) {
    const response = await fetch(`${api.baseUrl}/ventas/entregas/fluido/${fluidoId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  /**
   * Actualizar entrega existente (R8)
   */
  async actualizarEntrega(id, data) {
    return await api.put(`/ventas/entregas/${id}`, data);
  }

  /**
   * Completar entrega de tractor
   */
  async completarEntrega(id, data) {
    return await api.post(`/ventas/entregas/${id}/completar`, data);
  }

  async eliminarEntrega(id) {
    return await api.delete(`/ventas/entregas/${id}`);
  }

  /**
   * Obtener estadísticas de entregas
   */
  async obtenerEstadisticasEntregas() {
    return await api.get('/ventas/entregas/estadisticas');
  }

  // =====================================================
  // CHECKLIST DOCUMENTOS (AMX-VTA-FR-11)
  // =====================================================

  /**
   * Listar checklists de documentos
   */
  async listarChecklistDocumentos(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/ventas/checklist-documentos', cleanParams);
  }

  /**
   * Obtener checklist de documentos por ID
   */
  async obtenerChecklistDocumentos(id) {
    return await api.get(`/ventas/checklist-documentos/${id}`);
  }

  /**
   * Lookup para crear FR-11: aplica gate (tractor + FR-07 + sin FR-11 activo).
   * Errores con code: TRACTOR_NO_ENCONTRADO, ENTREGA_TRACTOR_REQUERIDA,
   * CHECKLIST_ENTREGA_YA_EXISTE.
   */
  async lookupTractorParaChecklist(numSerie) {
    return await api.get(`/ventas/checklist-documentos/lookup-tractor/${encodeURIComponent(numSerie)}`);
  }

  /**
   * Crear checklist de documentos
   */
  async crearChecklistDocumentos(data) {
    return await api.post('/ventas/checklist-documentos', data);
  }

  /**
   * Actualizar checklist de documentos
   */
  async actualizarChecklistDocumentos(id, data) {
    return await api.put(`/ventas/checklist-documentos/${id}`, data);
  }

  /**
   * Eliminar checklist de documentos
   */
  async eliminarChecklistDocumentos(id) {
    return await api.delete(`/ventas/checklist-documentos/${id}`);
  }

  // =====================================================
  // ENCUESTAS DE SATISFACCIÓN (AMX-VTA-FR-08)
  // =====================================================

  /**
   * Listar encuestas con filtros
   */
  async listarEncuestas(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/ventas/encuestas', cleanParams);
  }

  /**
   * Obtener encuesta por ID
   */
  async obtenerEncuesta(id) {
    return await api.get(`/ventas/encuestas/${id}`);
  }

  /**
   * Crear encuesta de satisfacción
   */
  async crearEncuesta(data) {
    return await api.post('/ventas/encuestas', data);
  }

  async actualizarEncuesta(id, data) {
    return await api.put(`/ventas/encuestas/${id}`, data);
  }

  async completarEncuesta(id) {
    return await api.post(`/ventas/encuestas/${id}/completar`);
  }

  async eliminarEncuesta(id) {
    return await api.delete(`/ventas/encuestas/${id}`);
  }

  /**
   * Obtener estadísticas de encuestas
   */
  async obtenerEstadisticasEncuestas() {
    return await api.get('/ventas/encuestas/estadisticas');
  }

  // =====================================================
  // CERTIFICADOS DE VENTA (AMX-VTA-FR-09)
  // =====================================================

  /**
   * Listar certificados con filtros
   */
  async listarCertificados(params = {}) {
    const cleanParams = this.limpiarParams(params);
    return await api.get('/ventas/certificados', cleanParams);
  }

  /**
   * Obtener certificado por ID
   */
  async obtenerCertificado(id) {
    return await api.get(`/ventas/certificados/${id}`);
  }

  /**
   * Crear certificado de venta
   */
  async crearCertificado(data) {
    return await api.post('/ventas/certificados', data);
  }

  /**
   * Actualizar certificado de venta
   */
  async actualizarCertificado(id, data) {
    return await api.put(`/ventas/certificados/${id}`, data);
  }

  /**
   * Eliminar certificado de venta
   */
  async eliminarCertificado(id) {
    return await api.delete(`/ventas/certificados/${id}`);
  }

  /**
   * Actualizar item de protocolo de entrega
   */
  async actualizarProtocolo(itemId, data) {
    const response = await fetch(`${api.baseUrl}/ventas/certificados/protocolo/${itemId}`, {
      method: 'PATCH',
      headers: api.getHeaders(),
      body: JSON.stringify(data)
    });
    return await api.handleResponse(response);
  }

  // =====================================================
  // PLANTILLAS
  // =====================================================

  /**
   * Obtener plantillas/constantes para formularios
   */
  async obtenerPlantillas() {
    return await api.get('/ventas/plantillas');
  }

  // =====================================================
  // UTILIDADES
  // =====================================================

  /**
   * Limpiar parámetros vacíos
   */
  limpiarParams(params) {
    const cleanParams = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        cleanParams[key] = params[key];
      }
    });
    return cleanParams;
  }
}

export default new VentasService();
